import { useCallback, useMemo, useState } from 'react';
import { notificationService } from '@/lib/services/notification.service';
import { CustomCard } from '../CustomCard';
import { AutoHeightIframe } from '../AutoHeightIframe';
import { getWidgetStores } from '@/entities/widget';
import {
  SectionPickerIframe,
  CopyEditDialog,
  replaceElementContent,
  removeElement,
  setElementAttribute,
  getElementAttribute,
  readLockState,
} from '@/entities/section-picker';
import type { ElementLockState, SectionPickerClickPayload } from '@/entities/section-picker';
import {
  applyLockChoice,
  EMPTY_LOCK_STATE,
  openedContentOf,
  savedContentOf,
} from './PreviewView.lock';
import { ImageEditDialog } from '@/shared/ui/ImageEditDialog';
import type { PreviewViewProps } from './PreviewView.types';
import { useTranslation } from 'react-i18next';

export function PreviewView({
  html,
  subjectLine,
  preheaderText,
  enableCopyEditing,
  enableImageEditing,
  useKnowledgeBaseImagesHook,
  onHtmlChange,
}: PreviewViewProps) {
  const { t } = useTranslation('contentTest');

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`.
  const { uiStore } = getWidgetStores();
  const { sidePanelLayoutMode } = uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [clickedElement, setClickedElement] = useState<SectionPickerClickPayload | null>(null);
  const [lockState, setLockState] = useState<ElementLockState>(EMPTY_LOCK_STATE);
  // Captured when the image dialog opens so a lock-only submit can echo the src back unchanged.
  const [clickedImageSrc, setClickedImageSrc] = useState('');

  const handleCopySubject = async () => {
    if (!subjectLine) return;
    await navigator.clipboard.writeText(subjectLine);
    notificationService.showSuccess(t('emailFlowWidget.previewView.subjectLineCopied'));
  };

  const handleCopyPreHeader = async () => {
    if (!preheaderText) return;
    await navigator.clipboard.writeText(preheaderText);
    notificationService.showSuccess(t('emailFlowWidget.previewView.preHeaderCopied'));
  };

  const handleElementClick = useCallback(
    (payload: SectionPickerClickPayload) => {
      // Image elements have no text content, so they route to the image picker instead of the
      // copy-edit dialog (which the text guard below would otherwise reject).
      if (enableImageEditing && payload.tagName === 'img') {
        setClickedElement(payload);
        setLockState(readLockState(html, payload.selector));
        // eslint-disable-next-line i18next/no-literal-string -- HTML attribute name
        setClickedImageSrc(getElementAttribute(html, payload.selector, 'src') ?? '');
        setImageDialogOpen(true);
        return;
      }
      if (!payload.fullTextContent) return;
      setClickedElement(payload);
      setLockState(readLockState(html, payload.selector));
      setDialogOpen(true);
    },
    [enableImageEditing, html],
  );

  function handleCopyEditConfirm(newContent: string, locked: boolean) {
    if (!clickedElement || !onHtmlChange) return;
    const withLock = applyLockChoice(html, clickedElement.selector, locked, lockState);
    if (withLock === null) {
      notificationService.showError(t('emailFlowWidget.previewView.failedToUpdateLockOnContent'));
      return;
    }

    // A lock-only submit must not touch the markup. `newContent` here would be the DOM-serialised
    // form of what the dialog was opened with, and splicing that back over the source is lossy: it
    // promotes escaped text to live markup, drops HTML comments (Outlook MSO conditionals among
    // them), and re-serialises attributes. Only replace content the user actually changed.
    let nextHtml = withLock;
    const contentChanged = newContent !== openedContentOf(clickedElement);
    if (contentChanged) {
      const result = replaceElementContent(
        withLock,
        clickedElement.selector,
        savedContentOf(clickedElement, newContent),
      );
      if (!result.success) {
        notificationService.showError(
          result.error ?? t('emailFlowWidget.previewView.failedToUpdateContent'),
        );
        return;
      }
      nextHtml = result.html;
    }

    onHtmlChange(nextHtml);
    setDialogOpen(false);
    setClickedElement(null);
    notificationService.showSuccess(
      contentChanged
        ? locked
          ? t('emailFlowWidget.previewView.contentUpdatedAndLocked')
          : t('emailFlowWidget.previewView.contentUpdated')
        : locked
          ? t('emailFlowWidget.previewView.contentLocked')
          : t('emailFlowWidget.previewView.contentUnlocked'),
    );
  }

  function handleImageEditConfirm(newSrc: string, locked: boolean) {
    if (!clickedElement || !onHtmlChange) return;
    const withLock = applyLockChoice(html, clickedElement.selector, locked, lockState);
    if (withLock === null) {
      notificationService.showError(t('emailFlowWidget.previewView.failedToUpdateLockOnImage'));
      return;
    }
    // eslint-disable-next-line i18next/no-literal-string -- HTML attribute name
    const result = setElementAttribute(withLock, clickedElement.selector, 'src', newSrc);
    if (!result.success) {
      notificationService.showError(
        result.error ?? t('emailFlowWidget.previewView.failedToUpdateImage'),
      );
      return;
    }
    onHtmlChange(result.html);
    setImageDialogOpen(false);
    setClickedElement(null);
    notificationService.showSuccess(
      locked
        ? t('emailFlowWidget.previewView.imageUpdatedAndLocked')
        : t('emailFlowWidget.previewView.imageUpdated'),
    );
  }

  function handleRemove() {
    if (!clickedElement || !onHtmlChange) return;
    const result = removeElement(html, clickedElement.selector);
    if (!result.success) {
      notificationService.showError(
        result.error ?? t('emailFlowWidget.previewView.failedToRemoveElement'),
      );
      return;
    }
    onHtmlChange(result.html);
    setDialogOpen(false);
    setImageDialogOpen(false);
    setClickedElement(null);
    notificationService.showSuccess(t('emailFlowWidget.previewView.elementRemoved'));
  }

  function handleCloseDialog() {
    setDialogOpen(false);
    setClickedElement(null);
    setLockState(EMPTY_LOCK_STATE);
  }

  function handleCloseImageDialog() {
    setImageDialogOpen(false);
    setClickedElement(null);
    setLockState(EMPTY_LOCK_STATE);
  }

  const imageLock = useMemo(
    () => ({
      checked: lockState.locked,
      disabled: lockState.locked && !lockState.canUnlock,
      disabledReason: lockState.lockedByTemplate
        ? t('emailFlowWidget.previewView.lockedByTheTemplate')
        : t('emailFlowWidget.previewView.lockedAsPartOfALargerSection'),
    }),
    [lockState, t],
  );

  const cards = (
    <>
      <CustomCard
        title={t('emailFlowWidget.previewView.subjectLine')}
        ariaLabel={t('emailFlowWidget.previewView.copySubjectLine')}
        // eslint-disable-next-line i18next/no-literal-string -- email-platform merge tag, resolved by the sending platform and must stay verbatim
        description={subjectLine || '{SubjectLine}'}
        onClick={() => void handleCopySubject()}
      />
      <CustomCard
        title={t('emailFlowWidget.previewView.preHeaderText')}
        ariaLabel={t('emailFlowWidget.previewView.copyPreHeaderText')}
        // eslint-disable-next-line i18next/no-literal-string -- email-platform merge tag, resolved by the sending platform and must stay verbatim
        description={preheaderText || '{PreHeaderText}'}
        onClick={() => void handleCopyPreHeader()}
      />
    </>
  );

  const subjectAndPreheader = isFullscreen ? (
    <div className="flex flex-col gap-4 pl-10 pb-4">{cards}</div>
  ) : (
    <div className="flex flex-col lg:flex-row gap-4 px-10 pb-4 w-full">{cards}</div>
  );

  const content = useMemo(
    () => (
      <div
        className={`rounded-3xl border border-black/5 py-13.5 px-25 mb-6 mr-10 ${
          isFullscreen ? 'ml-6' : 'ml-10'
        }`}
      >
        {enableCopyEditing || enableImageEditing ? (
          <SectionPickerIframe
            htmlContent={html}
            onElementClick={handleElementClick}
            pickerEnabled
            autoHeight
            hideTaggedHighlights
          />
        ) : (
          <AutoHeightIframe title={t('emailFlowWidget.previewView.emailPreview')} srcDoc={html} />
        )}
      </div>
    ),
    [html, isFullscreen, enableCopyEditing, enableImageEditing, handleElementClick, t],
  );

  return (
    <>
      <div className={isFullscreen ? 'flex flex-row gap-4' : ''}>
        <div className={isFullscreen ? 'w-125 shrink-0' : ''}>{subjectAndPreheader}</div>
        <div className={isFullscreen ? 'flex-1' : ''}>{content}</div>
      </div>

      {enableCopyEditing && (
        <CopyEditDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleCopyEditConfirm}
          onRemove={handleRemove}
          tagName={clickedElement?.tagName ?? ''}
          currentContent={clickedElement ? openedContentOf(clickedElement) : ''}
          hasChildElements={clickedElement?.hasChildElements ?? false}
          lockState={lockState}
        />
      )}

      {enableImageEditing && (
        <ImageEditDialog
          open={imageDialogOpen}
          onClose={handleCloseImageDialog}
          onConfirm={handleImageEditConfirm}
          onRemove={handleRemove}
          sources={['upload', 'kb']}
          useKnowledgeBaseImagesHook={useKnowledgeBaseImagesHook}
          lock={imageLock}
          currentSrc={clickedImageSrc}
        />
      )}
    </>
  );
}
